/**
 * Shared readers for an Engage job object. Engage stores form fields like the
 * job title as entries in the job's `headers[]` array (`{field, value}`), NOT
 * as top-level columns — so a lookup that only checks top-level keys misses
 * them and falls back to "Untitled" (AIVA-6987). These helpers read top-level
 * keys first, then the `headers[]` array, mirroring the prototype's
 * header-first `fetchEngageJobDetail`.
 */

type JobRecord = Record<string, unknown>;

/** First non-empty value among `keys` (strings, or coerced number/boolean). */
function pickTopLevel(obj: JobRecord, keys: readonly string[]): string {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "string" && v.length > 0) return v;
    if (typeof v === "number" || typeof v === "boolean") return String(v);
  }
  return "";
}

/**
 * Value of the first `headers[]` entry whose field name matches one of
 * `fieldNames` (case-insensitive). Engage uses `{field, value}` with assorted
 * casings (`field`/`FIELD`/`name`, `value`/`VALUE`).
 */
export function readHeaderValue(job: JobRecord, fieldNames: readonly string[]): string {
  if (!Array.isArray(job.headers)) return "";
  const wanted = new Set(fieldNames.map((f) => f.toUpperCase()));
  for (const h of job.headers as JobRecord[]) {
    const field = pickTopLevel(h, ["field", "FIELD", "name"]).toUpperCase();
    if (field && wanted.has(field)) {
      const value = pickTopLevel(h, ["value", "VALUE"]);
      if (value) return value;
    }
  }
  return "";
}

/**
 * The job title from top-level keys, else from the `JOB_TITLE` header entry.
 * Returns "" when none is found so callers can apply their own fallback.
 * (GetJob usually returns neither for these jobs — the active-list feed and its
 * cached title are the real source; this covers job types that do embed it.)
 */
export function readJobTitle(job: JobRecord): string {
  return (
    pickTopLevel(job, ["joB_TITLE", "JOB_TITLE", "title"]) ||
    readHeaderValue(job, ["JOB_TITLE", "TITLE"])
  );
}
